import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transactionAPI } from '../services/api';
import { Search, Plus, Edit, Trash2, FileText } from 'lucide-react';
import { formatDate } from '../utils/formatters';

const Engraving = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEngraving, setEditingEngraving] = useState(null);
  
  const [formData, setFormData] = useState({
    member_number: '',
    engraving_date: '',
    type: '',
    content: '',
    cost: '',
    status: 'pending',
    memo: ''
  });

  // 각자비 목록 조회
  const { data: engravings, isLoading } = useQuery({
    queryKey: ['engravings', currentPage, searchTerm, selectedType],
    queryFn: () => transactionAPI.getEngravings({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      type: selectedType
    }),
    keepPreviousData: true,
    select: (data) => {
      if (data?.data?.engravings) {
        return {
          data: data.data.engravings,
          pagination: data.data.pagination
        };
      } else if (data?.data) {
        return {
          data: Array.isArray(data.data) ? data.data : [],
          pagination: data.pagination
        };
      } else if (Array.isArray(data)) {
        return {
          data: data,
          pagination: null
        };
      }
      return {
        data: [],
        pagination: null
      };
    }
  });

  // 각자비 추가/수정 뮤테이션
  const mutation = useMutation({
    mutationFn: (data) => {
      if (editingEngraving) {
        return transactionAPI.updateEngraving(editingEngraving.id, data);
      } else {
        return transactionAPI.createEngraving(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['engravings']);
      showToast(editingEngraving ? '각자비가 수정되었습니다.' : '각자비가 등록되었습니다.', 'success');
      setShowAddModal(false);
      setEditingEngraving(null);
      resetForm();
    },
    onError: (error) => {
      showToast(error.message || '오류가 발생했습니다.', 'error');
    }
  });

  const resetForm = () => {
    setFormData({
      member_number: '',
      engraving_date: '',
      type: '',
      content: '',
      cost: '',
      status: 'pending',
      memo: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const handleEdit = (engraving) => {
    setEditingEngraving(engraving);
    setFormData({
      member_number: engraving.member_number,
      engraving_date: engraving.engraving_date,
      type: engraving.type,
      content: engraving.content,
      cost: engraving.cost,
      status: engraving.status,
      memo: engraving.memo
    });
    setShowAddModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      transactionAPI.deleteEngraving(id).then(() => {
        queryClient.invalidateQueries(['engravings']);
        showToast('각자비가 삭제되었습니다.', 'success');
      }).catch((error) => {
        showToast(error.message || '삭제 중 오류가 발생했습니다.', 'error');
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'in_progress':
        return 'status-progress';
      case 'pending':
        return 'status-waiting';
      default:
        return 'status-cancelled';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return '완료';
      case 'in_progress':
        return '진행중';
      case 'pending':
        return '대기';
      default:
        return '취소';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'initial':
        return '초각';
      case 're_engraving':
        return '재각';
      default:
        return '기타';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">각자비 관리</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <Plus size={20} />
          각자비 등록
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="card mb-6">
        <div className="grid grid-3" style={{ gap: '20px' }}>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="묘적번호로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input w-full pl-10"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              각자 유형
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="select w-full"
            >
              <option value="">전체 유형</option>
              <option value="initial">초각</option>
              <option value="re_engraving">재각</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary w-full"
            >
              <Plus size={20} />
              각자비 등록
            </button>
          </div>
        </div>
      </div>

      {/* 각자비 목록 테이블 */}
      <div className="card">
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : engravings?.data?.length === 0 ? (
          <div className="empty-state">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">각자비 내역이 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>각자일</th>
                    <th>묘적번호</th>
                    <th>각자 유형</th>
                    <th>각자 내용</th>
                    <th>비용</th>
                    <th>상태</th>
                    <th>메모</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {engravings?.data?.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.engraving_date)}</td>
                      <td className="font-medium">{item.member_number}</td>
                      <td>{getTypeText(item.type)}</td>
                      <td>{item.content}</td>
                      <td>₩{item.cost?.toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${getStatusColor(item.status)}`}>
                          {getStatusText(item.status)}
                        </span>
                      </td>
                      <td>{item.memo}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {engravings?.pagination && (
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-gray-700">
                  총 {engravings.pagination.total}개 중 {engravings.pagination.from}-{engravings.pagination.to}개
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                  >
                    이전
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-700">
                    {currentPage} / {engravings.pagination.last_page}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage >= engravings.pagination.last_page}
                    className="btn btn-secondary"
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 각자비 등록/수정 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="text-xl font-bold mb-4">
              {editingEngraving ? '각자비 수정' : '각자비 등록'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    묘적번호
                  </label>
                  <input
                    type="text"
                    value={formData.member_number}
                    onChange={(e) => setFormData({...formData, member_number: e.target.value})}
                    className="input w-full"
                    placeholder="묘적번호를 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    각자일
                  </label>
                  <input
                    type="date"
                    value={formData.engraving_date}
                    onChange={(e) => setFormData({...formData, engraving_date: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    각자 유형
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="select w-full"
                    required
                  >
                    <option value="">각자 유형을 선택하세요</option>
                    <option value="initial">초각</option>
                    <option value="re_engraving">재각</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    각자 내용
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    className="input w-full"
                    rows="4"
                    placeholder="각자할 내용을 입력하세요"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    비용
                  </label>
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: parseInt(e.target.value) || 0})}
                    className="input w-full"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    상태
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="select w-full"
                    required
                  >
                    <option value="pending">대기</option>
                    <option value="in_progress">진행중</option>
                    <option value="completed">완료</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    메모
                  </label>
                  <textarea
                    value={formData.memo}
                    onChange={(e) => setFormData({...formData, memo: e.target.value})}
                    className="input w-full"
                    rows="3"
                    placeholder="추가 메모를 입력하세요"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={mutation.isLoading}
                >
                  {mutation.isLoading ? '저장 중...' : (editingEngraving ? '수정' : '등록')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingEngraving(null);
                    resetForm();
                  }}
                  className="btn btn-secondary flex-1"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Engraving; 